import {
  defineStore,
} from "pinia";
import en from "../locales/en.json";
import de from "../locales/de.json";
import vn from "../locales/vn.json";

// Element Plus Locales
import elEn from "element-plus/dist/locale/en.mjs";
import elDe from "element-plus/dist/locale/de.mjs";
import elVi from "element-plus/dist/locale/vi.mjs";

export type Locale = "en" | "de" | "vn";

const LOCALES: Record<Locale, any> = {en, de, vn};
const EL_LOCALES: Record<Locale, any> = {en: elEn, de: elDe, vn: elVi};

export const useLocaleStore = defineStore("locale", () => {
  const currentLocale = ref<Locale>("en");
  const initialized = ref(false);

  const elLocale = computed(() => EL_LOCALES[currentLocale.value]);

  function setLocale(locale: Locale) {
    currentLocale.value = locale;
    if (typeof window !== "undefined") {
      localStorage.setItem("app_lang", locale);
      document.documentElement.lang = locale;
    }
  }

  function initialize() {
    if (initialized.value || typeof window === "undefined") {
      return;
    }

    const saved = localStorage.getItem("app_lang") as Locale | null;
    if (saved && LOCALES[saved]) {
      currentLocale.value = saved;
    } else {
      const browserLang = ((window.navigator as any)?.language || "en").split("-")[0].toLowerCase();
      if (browserLang === "de") {
        currentLocale.value = "de";
      } else if (browserLang === "vi" || browserLang === "vn") {
        currentLocale.value = "vn";
      } else if (browserLang === "en") {
        currentLocale.value = "en";
      } else {
        currentLocale.value = "en"; // Fallback
      }
    }

    document.documentElement.lang = currentLocale.value;
    initialized.value = true;
  }

  function lookup(locale: Locale, key: string): string | undefined {
    const keys = key.split(".");
    let value = LOCALES[locale];

    for (const k of keys) {
      if (value && typeof value === "object" && k in value) {
        value = value[k];
      } else {
        return undefined;
      }
    }

    return typeof value === "string" ? value : undefined;
  }

  /**
   * Picks one branch of a plural message written "one | many", or
   * "zero | one | many" — the vue-i18n convention the locale files were already
   * written in. Nothing here ever split on the pipe, so such a message rendered
   * with the pipe and every branch visible: "3 variable in content | 3 variables
   * in content".
   *
   * The branch is chosen by the `count` param. Two branches: 1 takes the first,
   * everything else the second. Three: zero gets its own wording. A message with
   * no pipe is returned untouched, so this cannot disturb any other string, and
   * without a numeric count there is nothing to choose on — the first branch
   * wins, as vue-i18n does.
   */
  function selectPlural(value: string, count?: unknown): string {
    if (!value.includes("|")) {
      return value;
    }
    const branches = value.split("|").map((branch) => branch.trim());
    if (typeof count !== "number" || !Number.isFinite(count)) {
      return branches[0] ?? value;
    }
    if (branches.length > 2) {
      return (count === 0 ? branches[0] : count === 1 ? branches[1] : branches[2]) ?? value;
    }
    return (count === 1 ? branches[0] : branches[1] ?? branches[0]) ?? value;
  }

  function translate(locale: Locale, key: string, params?: Record<string, any>): string {
    // Fall back to English for keys missing in the current locale,
    // then to the raw key if English is missing too
    const value = lookup(locale, key) ?? lookup("en", key);

    if (value === undefined) {
      return key;
    }

    const message = selectPlural(value, params?.count);

    if (params) {
      return Object.entries(params).reduce((acc, [k, v]) => {
        return acc.replace(new RegExp(`{${k}}`, "g"), String(v));
      }, message);
    }

    return message;
  }

  function t(key: string, params?: Record<string, any>): string {
    return translate(currentLocale.value, key, params);
  }

  function tForLocale(key: string, locale: Locale, params?: Record<string, any>): string {
    return translate(locale, key, params);
  }

  return {
    currentLocale,
    initialized,
    elLocale,
    setLocale,
    initialize,
    t,
    tForLocale,
  };
});
